import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import {
  nestApiCreateNote,
  nestApiUpdateNote,
  nestApiDeleteNote,
} from '@rabbit/bizproc/core';
import { useGetNotes } from '@rabbit/bizproc/react';
import { useIdentity } from '@rabbit/data/portal';
import {
  DTNote,
  NoteCategoryType,
  NoteEntityType,
  NoteType,
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
  UserUploadedDocument,
} from '@rabbit/data/types';
import {
  Button,
  formatUnixTime,
  Input,
  LabelledItemList,
  LabelledItemShape,
  Modal,
} from '@rabbit/elements/shared-components';
import { UserContext } from '@rabbit/sage/context/UserContext';
import { formatDate, titleCase } from '@rabbit/sage/utils/helpers';
import { Formik, Form } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Please enter a title'),
  description: Yup.string().trim().required('Please enter a description'),
  category: Yup.string().required('Please select a category'),
});

interface NoteShape {
  title: string;
  description: string;
  category: string;
  attachments: UserUploadedDocument[];
  date: number;
}
type NoteMode = 'add' | 'edit' | 'view';

export function Notes({
  entityId,
  entityType,
  showModal,
  setShowModal,
  onNotesCountChange,
}: {
  entityId: string;
  entityType: NoteEntityType;
  showModal: boolean;
  setShowModal: (v: boolean) => void;
  onNotesCountChange?: (count: number) => void;
}) {
  const { t } = useTranslation();
  const activePremiumTenant = t('tenantLink');
  const contextValues = useContext(UserContext);
  const isPartnerPersona =
    contextValues?.accessToken?.['W:' + activePremiumTenant] === 'U';
  const isWarrantorPersona =
    contextValues?.accessToken?.['W:' + activePremiumTenant] === 'S';

  const isPartner = isPartnerPersona && !isWarrantorPersona;
  const {
    data: { identity },
  } = useIdentity();
  const { notes, isLoading } = isPartner
    ? useGetNotes({
        tenantLink: activePremiumTenant,
        entityId,
        entityType,
        user_id: contextValues?.accessToken?.user_id,
        isPartner: true,
      }) ?? { notes: [], isLoading: false }
    : useGetNotes({
        tenantLink: activePremiumTenant,
        entityId,
        entityType,
      }) ?? { notes: [], isLoading: false };
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<NoteMode>('view');
  const [editNote, setEditNote] = useState<DTNote | null>(null);
  const [viewNote, setViewNote] = useState<DTNote | null>(null);

  const categoryColors: Record<string, string> = {
    complaint: 'bg-red-100 text-red-800',
    support: 'bg-blue-100 text-blue-800',
    feedback: 'bg-green-100 text-green-800',
    sales: 'bg-purple-100 text-purple-800',
    technical: 'bg-yellow-100 text-yellow-800',
  };

  const defaultCategoryStyle = 'bg-gray-100 text-gray-800';

  const categoryOptions = Object.values(NoteCategoryType).map((value) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    value: value,
  }));

  useEffect(() => {
    if (onNotesCountChange && Array.isArray(notes)) {
      onNotesCountChange(notes.length);
    }
  }, [notes, onNotesCountChange]);

  const onSubmit = async (values: NoteShape) => {
    try {
      setSubmitting(true);

      const warrantor = getRootPersonaFromLexicon(
        t(PrincipalsFieldName),
        PersonaTypeSingleLetter.Warrantor
      );

      const noteData = {
        title: values.title,
        content: values.description,
        interactionDate: new Date().getTime(),
        category: values.category as any,
        noteType: NoteType.GENERAL,
        entityType: entityType,
        entityId: entityId,
        tenantLink: activePremiumTenant,
        warrantor: warrantor,
        attachments: values.attachments,
        updatedBy: identity?.docid ?? '',
        updateByName: identity?.displayname ?? '',
      };

      if (mode === 'edit' && editNote) {
        const response = await nestApiUpdateNote({
          docid: editNote.docid,
          ...noteData,
        });
        if (response) {
          toast.success(t('Note updated successfully!'));
        } else {
          toast.error(t('Unable to save note'));
        }
      } else {
        const res = await nestApiCreateNote(noteData);
        if (res) {
          toast.success(t('Note created successfully!'));
        } else {
          toast.error(t('Unable to save note'));
        }
      }
      setMode('view');
    } catch (error) {
      console.error('NotesDetailContentSection: Error saving note:', error);
      toast.error(t('message.somethingWentWrongPleaseTryAgain'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteNoteItem = async (note: DTNote) => {
    try {
      await nestApiDeleteNote({ docid: note.docid });
      toast.success(t('Note deleted successfully!'));
    } catch (error) {
      toast.error(t('Unable to delete note'));
    }
  };

  return (
    <>
      {showModal && (
        <>
          <Modal
            kind="generic"
            settings={{
              title: t('general.notes'),
              handleClose: () => {
                setShowModal(false);
              },
              loading: loading,
            }}
            className="md:w-[640px]"
          >
            {(mode === 'add' || mode === 'edit') && (
              <Formik
                initialValues={{
                  title: mode === 'edit' && editNote ? editNote.title : '',
                  description:
                    mode === 'edit' && editNote ? editNote.content : '',
                  category:
                    mode === 'edit' && editNote ? editNote.category : '',
                  date:
                    mode === 'edit' && editNote
                      ? editNote.interactionDate
                      : Date.now(),
                  attachments:
                    'edit' && editNote ? editNote.attachments || [] : [],
                }}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({ errors, values }) => (
                  <Form className="flex flex-col gap-4 px-5 py-4">
                    <Input
                      type="text"
                      name="title"
                      label={t('Title') + '*'}
                      settings={{ placeholder: t('Enter note title') }}
                    />
                    <Input
                      type="select"
                      name="category"
                      label={t('Category') + '*'}
                      settings={{
                        options: categoryOptions,
                      }}
                    />
                    <Input
                      type="rich-text"
                      name="description"
                      label={t('Description') + '*'}
                      settings={{}}
                    />
                    <Input
                      type="file"
                      name="attachments"
                      label="Attachments"
                      settings={{
                        accepts: ['image/*', '.pdf'],
                        autoUpload: true,
                        multiple: true,
                      }}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Button kind="primary" type="submit" loading={submitting}>
                        {mode === 'edit' ? t('Save note') : t('Add note')}
                      </Button>
                      <Button
                        kind="outline_red"
                        type="button"
                        onClick={() => setMode('view')}
                      >
                        {t('Cancel')}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
            {mode === 'view' && (
              <>
                <div className="mt-4 flex justify-end px-4">
                  <Button
                    icon={<PlusIcon />}
                    kind="primary"
                    size={'sm'}
                    onClick={() => setMode('add')}
                  >
                    {t('Add note')}
                  </Button>
                </div>
                <div className="flex flex-col gap-4 p-4">
                  {notes.map((note: any) => (
                    <NoteDetail
                      key={note.docid}
                      note={note}
                      edit={() => {
                        setEditNote(note);
                        setMode('edit');
                      }}
                      deleteNote={() => deleteNoteItem(note)}
                      onView={() => setViewNote(note)}
                    />
                  ))}
                </div>
              </>
            )}
          </Modal>
          {viewNote && (
            <Modal
              kind="generic"
              settings={{
                title: t('View Note'),
                handleClose: () => setViewNote(null),
              }}
              className="lg:w-[600px]"
            >
              <div className="flex flex-col gap-4 p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('Date')}</p>
                    <p className="mt-1 text-base">
                      {formatDate(new Date(viewNote.interactionDate))}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('Title')}</p>
                    <p className="mt-1 text-lg font-medium">{viewNote.title}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('Category')}</p>
                    <div className="mt-1">
                      <span
                        className={`inline-flex rounded-md px-3 py-1 text-sm font-medium ${
                          categoryColors[viewNote.category] ??
                          defaultCategoryStyle
                        }`}
                      >
                        {viewNote.category.charAt(0).toUpperCase() +
                          viewNote.category.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('Description')}</p>
                  <div className="mt-1 rounded-md bg-gray-50 p-4">
                    <div
                      dangerouslySetInnerHTML={{ __html: viewNote.content }}
                    />
                  </div>
                </div>
                <div className="border-t pt-4 text-sm text-gray-500">
                  {`${t('general.createdBy')}: ${viewNote?.updateByName || ''} |
                  ${t('general.createdAt')}: ${formatUnixTime(
                    viewNote.tcreate,
                    'dd/MM/yyyy'
                  )} |
                  ${t('general.modifiedAt')}: ${formatUnixTime(
                    viewNote.tupdate || viewNote.tcreate,
                    'dd/MM/yyyy HH:mm a'
                  )}`}
                </div>

                <div className="mt-4 flex justify-end gap-3 border-t pt-4">
                  <Button
                    kind="outline"
                    onClick={() => {
                      setViewNote(null);
                      setEditNote(viewNote);
                      setMode('edit');
                    }}
                  >
                    {t('Edit')}
                  </Button>
                  <Button kind="outline_red" onClick={() => setViewNote(null)}>
                    {t('Close')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  );
}

function NoteDetail({
  note,
  edit,
  deleteNote,
  onView,
}: {
  note: DTNote;
  edit: () => void;
  deleteNote: () => void;
  onView: () => void;
}) {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { t } = useTranslation();
  const items: LabelledItemShape[] = [
    {
      label: t('general.createdAt'),
      value: formatDate(new Date(note.tcreate)),
    },
    { label: t('general.createdBy'), value: note.updateByName || '' },
    { label: t('general.category'), value: titleCase(note.category) },
  ];
  const confirmationModalSettings = {
    kind: 'pop-up' as const,
    settings: {
      text: t('message.confirmDeleteNote'),
      primaryButtonText: t('general.yes'),
      outlineButtonText: t('general.cancel'),
      handleClose: () => setIsConfirmationModalOpen(false),
      handlePrimaryClick: async () => deleteNote(),
      handleOutlineClick: () => setIsConfirmationModalOpen(false),
    },
  };
  return (
    <div className="relative flex flex-col gap-1 rounded-lg border border-gray-200 p-4 hover:border-gray-400">
      <div className="absolute top-2 right-2 flex gap-2">
        <Button kind="primary" size="sm" onClick={() => edit()}>
          <PencilIcon className="h-4 w-4 text-white" />
        </Button>
        <Button
          kind="red"
          size="sm"
          onClick={() => setIsConfirmationModalOpen(true)}
        >
          <TrashIcon className="h-4 w-4 text-white" />
        </Button>
      </div>
      <div className="font-nunito text-large font-bold">{note.title}</div>
      <div
        className="font-nunito line-clamp-2 text-base text-gray-600"
        dangerouslySetInnerHTML={{
          __html: note.content,
        }}
      />
      <div className="mt-2">
        {note.attachments?.map((file, index) => (
          <div
            className="mb-4 flex flex-col rounded-lg border border-gray-200 p-2"
            key={'upload file ' + index}
          >
            <div className="grid grid-cols-[40px_1fr_40px] gap-2">
              <div className="bg-primary flex items-center justify-center">
                <img src={file.url} className="text-primary-700 h-8 w-8" />
              </div>
              <div className="flex flex-col">
                <div className="font-nunito text-base font-bold">
                  {file.ogFilename}
                </div>
                <div className="font-nunito text-sm text-gray-500">
                  {file.metadata.timeCreated}
                </div>
              </div>
              <div className="bg-primary flex cursor-pointer items-center justify-center">
                <ArrowTopRightOnSquareIcon
                  className="text-primary-800 h-6 w-6"
                  onClick={() => window.open(file.url, '_blank')}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <LabelledItemList
        items={items}
        listWrapperClasses="mt-2 grid grid-cols-3 gap-4"
        itemProps={{ labelClasses: 'text-sm text-gray-500 font-nunito' }}
      />
      {isConfirmationModalOpen && (
        <Modal
          className="h-fit w-[350px] rounded-md bg-white"
          kind={confirmationModalSettings.kind}
          settings={confirmationModalSettings.settings}
        />
      )}
    </div>
  );
}

export default Notes;
